import React from 'react';
import { HealthData, HealthPillar } from '../../../utils/complianceCalculator';

interface Props {
    health: HealthData;
}

const PillarCard: React.FC<{ title: string; pillar: HealthPillar; icon: string }> = ({ title, pillar, icon }) => {
    const isPass = pillar.status === 'PASS';
    const isWarn = pillar.status === 'WARN';

    const bgColor = isPass ? 'bg-green-50' : isWarn ? 'bg-yellow-50' : 'bg-red-50';
    const textColor = isPass ? 'text-green-800' : isWarn ? 'text-yellow-800' : 'text-red-800';
    const borderColor = isPass ? 'border-green-200' : isWarn ? 'border-yellow-200' : 'border-red-200';

    return (
        <div className={`border rounded-lg p-4 ${bgColor} ${borderColor} transition-all hover:shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                    <i className={`fas ${icon} mr-2 ${textColor}`}></i>
                    <h4 className={`font-semibold ${textColor}`}>{title}</h4>
                </div>
                <span className={`text-sm font-bold ${textColor}`}>{pillar.score}/{pillar.max}</span>
            </div>
            {/* Items list (brief) */}
            <div className="space-y-1">
                {pillar.items.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                        <span>{item.type || item.description || 'Check'}</span>
                        <span>
                            {item.status === 'VALID' || item.score === 100
                                ? <i className="fas fa-check-circle text-green-500"></i>
                                : <i className="fas fa-exclamation-circle text-red-500"></i>
                            }
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const HealthScoreWidget: React.FC<Props> = ({ health }) => {
    if (!health || !health.pillars) return null;

    const { totalScore, status } = health;

    // Status color
    const statusColor = totalScore >= 90 ? 'text-green-600' :
        totalScore >= 70 ? 'text-blue-600' :
            totalScore >= 50 ? 'text-yellow-600' : 'text-red-600';

    const ringColor = totalScore >= 90 ? 'border-green-500' :
        totalScore >= 70 ? 'border-blue-500' :
            totalScore >= 50 ? 'border-yellow-500' : 'border-red-500';

    return (
        <div className="bg-white shadow rounded-lg p-6 mb-8 border border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Litigation Readiness & Health Score</h2>
                    <p className="text-gray-500 text-sm mt-1">
                        AI-driven analysis of your legal exposure and compliance health.
                    </p>
                </div>

                {/* Score Circle */}
                <div className="flex items-center mt-4 md:mt-0">
                    <div className={`relative flex items-center justify-center w-20 h-20 rounded-full border-4 ${ringColor} bg-gray-50`}>
                        <span className={`text-2xl font-bold ${statusColor}`}>{totalScore}%</span>
                    </div>
                    <div className="ml-4">
                        <div className={`text-lg font-bold ${statusColor}`}>{status}</div>
                        <div className="text-xs text-gray-400">Values updated live</div>
                    </div>
                </div>
            </div>

            {/* Pillars Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <PillarCard
                    title="Critical Docs"
                    pillar={health.pillars.criticalDocs}
                    icon="fa-file-medical"
                />
                <PillarCard
                    title="Syntax & Wording"
                    pillar={health.pillars.syntaxWording}
                    icon="fa-paragraph" // NLP
                />
                <PillarCard
                    title="Investigatory"
                    pillar={health.pillars.investigatory}
                    icon="fa-search"
                />
                <PillarCard
                    title="Admin Hygiene"
                    pillar={health.pillars.adminHygiene}
                    icon="fa-clipboard-check"
                />
            </div>

            {/* Only show syntax warning if tenancy agreement exists but has issues (WARN), not when missing (FAIL) */}
            {health.pillars.syntaxWording.status === 'WARN' && (
                <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <i className="fas fa-exclamation-triangle text-yellow-500"></i>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                <strong>Warning:</strong> Syntax analysis pending for your Tenancy Agreement. Please upload and analyze to ensure Section 21 compliance.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
